<template>
  <bottom-sheet
    :visible="visible"
    title="Filter by status"
    @change="$emit('change', $event)"
  >
    <div
      class="status-sheet"
      role="radiogroup"
      aria-label="Job status"
    >
      <template v-for="group in groups">
        <p
          v-if="group.label"
          :key="`${group.key}-label`"
          class="status-sheet__group"
        >
          {{ group.label }}
        </p>
        <button
          v-for="option in group.options"
          :key="option.id"
          type="button"
          class="status-sheet__option"
          :class="{ 'status-sheet__option--active': option.id === status }"
          role="radio"
          :aria-checked="option.id === status ? 'true' : 'false'"
          @click="select(option)"
        >
          <span
            class="status-sheet__dot"
            :class="`status-sheet__dot--${dotVariant(option)}`"
          />
          <span class="status-sheet__label">{{ option.value }}</span>
          <GjIcon
            v-if="option.id === status"
            class="status-sheet__check"
            name="Check"
            size="18"
          />
        </button>
      </template>
    </div>
  </bottom-sheet>
</template>

<script>
import BottomSheet from '@/components/popup/BottomSheet.vue'
import { jobStatusMeta } from '../job-status'

/**
 * Status picker for the jobs list on mobile. The API returns a dozen statuses, so
 * they are grouped by what they mean (still running vs finished) instead of being
 * laid out as a wall of chips in the page header.
 */
export default {
  name: 'JobStatusFilterSheet',
  components: {
    BottomSheet,
  },
  model: {
    prop: 'visible',
    event: 'change',
  },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    status: {
      type: Number,
      default: 0,
    },
    statusOptions: {
      type: Array,
      default: () => [],
    },
  },
  computed: {
    // Grouped from the status meta rather than from ids, so a status added to the
    // API later still lands in a sensible section.
    groups() {
      const general = []
      const running = []
      const finished = []

      this.statusOptions.forEach(option => {
        const meta = jobStatusMeta(option.value)
        if (meta.state === 'running') running.push(option)
        else if (meta.ended) finished.push(option)
        else general.push(option)
      })

      return [
        { key: 'general', label: '', options: general },
        { key: 'running', label: 'In progress', options: running },
        { key: 'finished', label: 'Finished', options: finished },
      ].filter(group => group.options.length)
    },
  },
  methods: {
    dotVariant(option) {
      return jobStatusMeta(option.value).variant
    },
    select(option) {
      if (option.id !== this.status) this.$emit('update:status', option.id)
      this.$emit('change', false)
    },
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/scss/variables/variables';

.status-sheet {
  padding: 0 8px 8px;

  &__group {
    margin: 12px 8px 4px;
    font-size: 11px;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: 0.4px;
    color: $blue-800;
  }

  &__option {
    display: flex;
    align-items: center;
    width: 100%;
    min-height: 48px;
    padding: 0 8px;
    border: none;
    border-radius: 8px;
    background-color: transparent;
    font-size: 14px;
    text-align: left;
    color: $blue-900;

    &--active {
      background-color: $blue-400;
      font-weight: 600;
      color: $primary;
    }
  }

  &__dot {
    flex: 0 0 auto;
    width: 8px;
    height: 8px;
    margin-right: 12px;
    border-radius: 50%;
    background-color: $gray-124;

    &--primary {
      background-color: $primary;
    }
    &--success {
      background-color: $success;
    }
    &--warning {
      background-color: $warning;
    }
    &--danger {
      background-color: $danger;
    }
  }

  &__label {
    flex: 1 1 auto;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  &__check {
    flex: 0 0 auto;
    margin-left: 8px;
    color: $primary;
  }
}
</style>
